/**
 * The rung whose answer is a place, not a button.
 *
 * The subdivision mode's rung 2 puts four of a state's major cities on the
 * globe as numbered dots and asks which one is the capital. That question is
 * answered by TAPPING THE GLOBE, so this component renders no options: the
 * globe is the option grid.
 *
 * What it does render is the candidate list, and the reason it is worth a
 * component is the constraint on that list.
 *
 * ===========================================================================
 * WHY THE NAMES ARE HERE AND THE DOTS ARE NOT LABELLED
 * ===========================================================================
 *
 * Four unexplained dots and "tap the capital" is not a geography question, it
 * is a lottery with no way in — a player who knows perfectly well that Ohio's
 * capital is Columbus has nothing to do with that knowledge. Naming the dots
 * on the map is the opposite failure: then the question is only whether you can
 * read, and the map has answered it for you.
 *
 * So the four names are given, ALPHABETICALLY, and the dots are given, in
 * shuffled option order, and the player supplies the one thing neither array
 * contains: which name goes where. The sort happens on the server
 * (`publicRound`), not here — a client that received the pairing and merely
 * chose not to display it would still be shipping the answer.
 *
 * Invariant 7 gets the same answer for free: there is still no text on the
 * globe. The names are in the HUD, where every other word in this game is.
 */

import type { LngLat } from '@geogame/shared';

export interface MapChoiceProbeProps {
  /** Candidate names, alphabetical and deliberately not aligned to the dots. */
  readonly names: readonly string[];
  /** Dot positions, in option order. Rendered by the globe, counted here. */
  readonly markers: readonly LngLat[];
  /** True once an answer is in flight — the globe stops taking taps. */
  readonly awaiting: boolean;
}

export function MapChoiceProbe({
  names,
  markers,
  awaiting,
}: MapChoiceProbeProps): React.JSX.Element {
  return (
    <div className="gg-mapchoice">
      <p className="gg-mapchoice-hint">
        {awaiting ? 'Answer locked. Grading…' : `Tap one of the ${markers.length} dots on the map.`}
      </p>
      <ul className="gg-mapchoice-names">
        {names.map((name) => (
          <li key={name} className="gg-mapchoice-name">
            {name}
          </li>
        ))}
      </ul>
      <p className="gg-mapchoice-note">
        These are the four cities on the map, in alphabetical order. Which is which is the
        question.
      </p>
    </div>
  );
}
